import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import PageHero from "@/components/site/PageHero";
import CTAStrip from "@/components/site/CTAStrip";
import RelatedLinks from "@/components/site/RelatedLinks";
import { useSEO, useJsonLd, SITE_URL, breadcrumbSchema } from "@/lib/seo";
import { linkifyGoAndTrack } from "@/lib/goAndTrack";

const FAQ = [
  {
    q: "What is a disposable package tracker?",
    a: "A disposable package tracker is a single-use tracking device applied directly to a parcel, carton, or pallet to provide location — and, depending on the device, condition data — during one shipment. It is not recovered, recharged, or returned. Disposable package trackers are also called disposable smart labels, single-use trackers, or (in buyer vocabulary) disposable GPS trackers. Note that not every disposable tracker contains a GNSS/GPS receiver — see the complete category guide for how each term applies.",
  },
  {
    q: "What is a disposable smart label?",
    a: "A disposable smart label is a single-use tracking device that is applied directly to a shipment, parcel, carton, or pallet, and discarded at the end of the journey. It delivers shipment-visibility data without the operational overhead of recovering reusable hardware. Many buyers also search for this product as a disposable GPS tracker, disposable GPS tracking sticker, or package tracking device.",
  },
  {
    q: "Are disposable smart labels the same as disposable GPS trackers?",
    a: "In practice, yes. 'Disposable GPS tracker' and 'disposable GPS tracking sticker' are the common search terms for what the industry calls a disposable smart label. Most modern single-use shipment tracking devices use Bluetooth Low Energy (BLE) rather than dedicated GPS hardware — but the operational outcome is the same: location, dwell, and exception data for one shipment journey.",
  },
  {
    q: "Can I use disposable smart labels as a package tracking device?",
    a: "Yes — disposable smart labels are purpose-built as package tracking devices and shipment tracking devices. They are small enough to apply directly to a carton, parcel, or pallet, and are designed to deliver one clean dataset per package without any recovery process.",
  },
  {
    q: "What's the difference between a GPS tracker for packages and a Bluetooth smart label?",
    a: "Most disposable 'GPS trackers for packages' on the market are actually BLE-based smart labels. True cellular-GPS trackers exist but tend to be larger, more expensive, and more power-hungry. BLE-based disposable smart labels deliver the operational outcome buyers want — package-level location, dwell, and exception data — in a thinner, cheaper, single-use form factor.",
  },
  {
    q: "What are these called when I'm searching for them?",
    a: "Common search terms include: disposable GPS tracker, GPS tracker for packages, GPS package tracking devices, disposable GPS tracking stickers, package tracking device, tracking device for packages, small tracking devices for packages, and shipment tracking devices. They all map to the same category — single-use, peel-and-stick smart labels for shipment monitoring.",
  },
  {
    q: "When should I use disposable smart labels instead of reusable trackers?",
    a: "Disposable labels fit best when the shipment terminates at a customer or partner site, when recovery of reusable trackers is unreliable, when running a pilot or seasonal programme, or when finance prefers per-shipment OpEx over CapEx hardware ownership.",
  },
  {
    q: "How do disposable smart labels integrate with my systems?",
    a: "Data from the labels flows into GoAndTrack, which exposes APIs and webhooks for integration with TMS, WMS, and ERP environments. Integration is supported as part of pilot and rollout work.",
  },
  {
    q: "Can I run a small pilot before committing?",
    a: "Yes. A structured pilot is the recommended starting point. We help you scope the lane, deploy disposable labels, and review results against your KPIs — without CapEx commitment or hardware fleet procurement.",
  },
  {
    q: "What kind of shipments are these labels best suited for?",
    a: "High-value shipments where independent journey evidence matters, single-trip lanes where recovery is impractical, partner-heavy networks, returns flows, and pilot or programme work where time-to-deploy is critical.",
  },
  {
    q: "What does the activation process look like?",
    a: "Activation is designed for the dispatch floor. Labels are applied, dispatched, and visible in GoAndTrack — no on-site engineering required.",
  },
  {
    q: "What happens to the label at end of journey?",
    a: "It is disposed of at the shipment endpoint. There is no recovery process, no return shipment of hardware, and no charging or pairing cycle.",
  },
];

export default function FAQs() {
  useSEO({
    title: "Disposable Smart Labels — Frequently Asked Questions",
    description:
      "Common questions about disposable smart labels, disposable GPS trackers, package tracking devices, BLE technology, pilots, and GoAndTrack platform support.",
    canonical: "/faqs",
  });
  useJsonLd("faqpage", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    url: `${SITE_URL}/faqs`,
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });
  useJsonLd(
    "breadcrumb-faqs",
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "FAQs" },
    ]),
  );

  return (
    <div data-testid="page-faqs">
      <PageHero
        eyebrow="FAQs"
        title="Frequently Asked Questions"
        subtitle="Practical answers for operations, supply chain, and innovation teams evaluating disposable smart labels."
      />
      <section className="section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow mb-4">Browse</p>
            <p className="body-text">
              If you don&apos;t see your question here, a specialist is happy to
              walk you through specifics for your shipments and lanes.
            </p>
            <Link to="/request-a-trial" className="btn-primary mt-8" data-testid="faq-cta">
              Speak to an expert <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQ.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-line"
                >
                  <AccordionTrigger
                    className="text-left font-heading text-lg font-medium py-5 hover:text-brand"
                    data-testid={`faq-trigger-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] text-ink-muted leading-relaxed pb-6">
                    {linkifyGoAndTrack(f.a)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
      <RelatedLinks
        title="Explore the category"
        links={[
          { to: "/disposable-package-tracker", label: "Disposable Package Tracker — complete category guide" },
          { to: "/gps-vs-ble-vs-cellular", label: "GPS vs BLE vs Cellular — technology comparison" },
          { to: "/glossary", label: "Glossary — disposable package tracker terminology" },
          { to: "/disposable-vs-reusable-trackers", label: "Disposable vs Reusable — full comparison" },
        ]}
      />
      <CTAStrip />
    </div>
  );
}
